"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile, faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { Button } from "@/components/ui/button/Button";
import { CASE_DOCUMENT_ACCEPT } from "@/components/case-checking/caseDocumentAccept";
import { DocumentClassificationStatusBadge } from "@/components/case-checking/caseCheckingStatusBadges";

import type { NewCaseUploadedFile } from "./newCaseFileUploadTypes";

type CaseNewCaseFileUploadProps = {
  files: NewCaseUploadedFile[];
  isProcessing: boolean;
  processingStep: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPickFiles: () => void;
  error?: string | null;
  description?: string;
  testId?: string;
};

export function CaseNewCaseFileUpload({
  files,
  isProcessing,
  processingStep,
  fileInputRef,
  onFileSelect,
  onPickFiles,
  error,
  description = "Upload all case files: Fact Find, File Note, Suitability Report, Disclosures",
  testId = "case-new-case-file-upload",
}: CaseNewCaseFileUploadProps) {
  return (
    <div className="space-y-4" data-testid={testId}>
      {error ? (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      ) : null}

      {isProcessing && processingStep ? (
        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg flex items-center gap-3">
          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-indigo-500 animate-spin" />
          <p className="text-indigo-700 dark:text-indigo-300 text-sm font-medium">{processingStep}</p>
        </div>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={CASE_DOCUMENT_ACCEPT}
        onChange={onFileSelect}
        className="hidden"
        data-testid={`${testId}-input`}
      />

      {files.length === 0 ? (
        <button
          type="button"
          className="w-full border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors"
          onClick={onPickFiles}
          data-testid={`${testId}-dropzone`}
        >
          <FontAwesomeIcon icon={faFile} className="w-10 h-10 text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-300 font-medium">Drop files here or click to upload</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</p>
        </button>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {files.map((file, i) => (
              <div
                key={`${file.name}-${i}`}
                className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
              >
                <FontAwesomeIcon icon={faFile} className="w-4 h-4 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 dark:text-white text-sm truncate">{file.name}</p>
                </div>
                {file.status === "classified" || file.status === "uploaded" ? (
                  <>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{file.classification}</span>
                    <DocumentClassificationStatusBadge valid size="sm" />
                  </>
                ) : file.status === "uploading" || file.status === "classifying" ? (
                  <>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{file.classification}</span>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-slate-400 animate-spin" />
                  </>
                ) : file.status === "pending" && file.classification === "Upload failed" ? (
                  <>
                    <span className="text-xs text-amber-600 dark:text-amber-400">{file.classification}</span>
                    <DocumentClassificationStatusBadge valid={false} size="sm" />
                  </>
                ) : file.status === "pending" ? (
                  <span className="text-xs text-slate-400 dark:text-slate-500">{file.classification}</span>
                ) : null}
              </div>
            ))}
          </div>

          {!isProcessing ? (
            <Button
              plain
              type="button"
              onClick={onPickFiles}
              className="w-full py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-500 dark:text-slate-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors"
            >
              + Add more files
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
